/**
 * The figures of the Threat Index, the merge of the Entity Risk Index and the Effective Blast Radius (client notes 11/2, and
 * 10/26 items 28 and 31). Pure, so a test can state what each should be.
 *
 *   Risk Value   1 to 100: the risk score the backend gives the asset, kept in range (10/26, item 31).
 *   Asset Value  up to 100: what the asset is worth (`lib/assetValue.ts`).
 *   Blast Radius = Asset Value x Risk Value, written on a scale of 1 to 50 (10/26, item 28). The two factors give up to
 *                  10,000, so the product is divided by 200 and kept at least 1. The notes' own factors (a Risk of 5, 8 or 10)
 *                  would give hundreds, which no scale of 50 holds, so the scale is read from the two 100-point factors.
 *   Rank         where the asset stands by Risk Value, now and seven days ago, so the table can say who rose and who fell.
 */
import { toScoreNumber } from '../components/c3/eriUtils';
import { ASSET_VALUE_MAX } from './assetValue';

export const RISK_VALUE_MIN = 1;
export const RISK_VALUE_MAX = 100;
export const BLAST_RADIUS_MIN = 1;
export const BLAST_RADIUS_MAX = 50;

/** A risk score as a Risk Value: a whole number from 1 to 100, or null when the asset has no score. */
export function riskValueOf(score: unknown): number | null {
  const n = toScoreNumber(score);
  if (n == null) return null;
  return Math.min(RISK_VALUE_MAX, Math.max(RISK_VALUE_MIN, Math.round(n)));
}

/** Asset Value x Risk Value on the scale of 1 to 50. */
export function blastRadiusOf(assetValue: number, riskValue: number): number {
  const scale = (ASSET_VALUE_MAX * RISK_VALUE_MAX) / BLAST_RADIUS_MAX;
  const value = Math.round((assetValue * riskValue) / scale);
  return Math.min(BLAST_RADIUS_MAX, Math.max(BLAST_RADIUS_MIN, value));
}

export interface RiskStanding {
  /** 1 is the riskiest asset. */
  rank: number;
  /** Where it stood seven days ago; null when the records say nothing of the past. */
  previousRank: number | null;
  /** Places climbed since then: positive when it rose, negative when it fell; null when unknown. */
  movement: number | null;
}

export interface RankInput {
  id: string;
  score: unknown;
  previousScore?: unknown;
  /** The change over seven days, used when there is no previous score. */
  delta?: unknown;
}

/** The standing of every asset by Risk Value, now and seven days ago. */
export function riskStandings(rows: RankInput[]): Map<string, RiskStanding> {
  const now = (row: RankInput) => riskValueOf(row.score) ?? 0;
  const then = (row: RankInput): number | null => {
    const previous = toScoreNumber(row.previousScore);
    if (previous != null) return previous;
    const delta = toScoreNumber(row.delta);
    const current = toScoreNumber(row.score);
    return delta != null && current != null ? current - delta : null;
  };
  const rankBy = (value: (row: RankInput) => number) => {
    const ordered = [...rows].sort((a, b) => value(b) - value(a) || a.id.localeCompare(b.id));
    return new Map(ordered.map((row, index) => [row.id, index + 1]));
  };
  const current = rankBy(now);
  // An asset the past says nothing of keeps its present value, so it neither rises nor falls against the rest.
  const past = rankBy((row) => then(row) ?? now(row));
  const standings = new Map<string, RiskStanding>();
  for (const row of rows) {
    const rank = current.get(row.id) as number;
    const known = then(row) != null;
    const previousRank = known ? (past.get(row.id) as number) : null;
    standings.set(row.id, { rank, previousRank, movement: previousRank == null ? null : previousRank - rank });
  }
  return standings;
}
